import { useEffect, useMemo, useRef, useState } from 'react';
import { ELEMENTS, type ElementInfo } from '../elements';
import {
  VizCard,
  NowYouTry,
  VIZ_FOCUS,
  DARK_INK,
  DARK_INK_SOFT,
  usePrefersReducedMotion,
  cueIsActive,
  cueHighlights,
  type VizCue,
} from '../vizShared';

// PeriodicTrends — a compact periodic-table grid that ILLUSTRATES a directional
// periodic trend (atomic radius, 1st ionization energy, electronegativity) with
//   • a continuous HEAT GRADIENT over the cells driven by the trend's direction
//     across periods and down groups,
//   • two big DIRECTIONAL ARROWS (across a period + down a group) showing where
//     the property increases, and
//   • a one-line RULE caption ("increases up and to the right").
// A selector switches between the three trends. The heat value for each cell is
// derived purely from its grid position (group/period) along the trend's
// direction vector — so it is always the textbook-correct gradient and can never
// throw on a missing per-element value.
//
// config: { trend?: 'atomic_radius'|'ionization_energy'|'electronegativity', title? }
// Cue: cue.highlight a trend name switches to it; cue.step (0-2) selects a trend;
// cue.progress (0-1) drives the self-demo reveal of the heat + arrows.

export type PeriodicTrendKey =
  | 'atomic_radius'
  | 'ionization_energy'
  | 'electronegativity';

export interface PeriodicTrendsConfig {
  trend?: PeriodicTrendKey;
  title?: string;
}

interface Props {
  config?: PeriodicTrendsConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

interface TrendDef {
  key: PeriodicTrendKey;
  label: string;
  // Rule caption shown under the table.
  rule: string;
  // Longer one-liner explaining WHY.
  blurb: string;
  // Direction the property INCREASES, expressed as grid deltas:
  //   acrossSign: +1 → increases left→right (toward group 18); -1 → right→left.
  //   downSign:   +1 → increases top→bottom (down a group);   -1 → bottom→top.
  acrossSign: 1 | -1;
  downSign: 1 | -1;
}

const TRENDS: TrendDef[] = [
  {
    key: 'atomic_radius',
    label: 'Atomic radius',
    rule: 'Increases down and to the left',
    blurb:
      'Down a group new shells are added (bigger); across a period the growing nuclear charge pulls electrons in (smaller).',
    acrossSign: -1,
    downSign: 1,
  },
  {
    key: 'ionization_energy',
    label: 'Ionization energy',
    rule: 'Increases up and to the right',
    blurb:
      'A higher nuclear pull and smaller radius hold the outer electron tighter, so more energy is needed to remove it.',
    acrossSign: 1,
    downSign: -1,
  },
  {
    key: 'electronegativity',
    label: 'Electronegativity',
    rule: 'Increases up and to the right',
    blurb:
      'Smaller, higher-charge atoms attract a shared bonding pair more strongly — fluorine (top-right) is the most electronegative.',
    acrossSign: 1,
    downSign: -1,
  },
];

const TREND_BY_KEY: Record<PeriodicTrendKey, TrendDef> = Object.fromEntries(
  TRENDS.map((t) => [t.key, t]),
) as Record<PeriodicTrendKey, TrendDef>;

// Map free-text cue tokens to a trend key (tolerate spaces / hyphens / aliases).
const TREND_ALIASES: Record<string, PeriodicTrendKey> = {
  atomic_radius: 'atomic_radius',
  'atomic radius': 'atomic_radius',
  'atomic-radius': 'atomic_radius',
  radius: 'atomic_radius',
  size: 'atomic_radius',
  'atomic size': 'atomic_radius',
  ionization_energy: 'ionization_energy',
  'ionization energy': 'ionization_energy',
  'ionisation energy': 'ionization_energy',
  ionization: 'ionization_energy',
  ie: 'ionization_energy',
  electronegativity: 'electronegativity',
  electroneg: 'electronegativity',
  en: 'electronegativity',
};

function resolveTrendToken(tokens: string[]): PeriodicTrendKey | null {
  for (const t of tokens) {
    const hit = TREND_ALIASES[t];
    if (hit) return hit;
  }
  return null;
}

// Grid placement: main block uses group (1-18) × period (1-7). The f-block
// (group === null) is drawn as a separate two-row strip below, offset to start
// under group 3 — the standard textbook layout.
interface Cell extends ElementInfo {
  col: number; // 1-based grid column (1..18 for the strip; 3.. for f-block)
  row: number; // 1-based grid row within its section
  fBlock: boolean;
}

const F_BLOCK_START_COL = 3;

function buildCells(): { main: Cell[]; fBlock: Cell[] } {
  const main: Cell[] = [];
  const lanth: ElementInfo[] = [];
  const actin: ElementInfo[] = [];
  for (const e of ELEMENTS) {
    if (e.group == null) {
      (e.category === 'actinide' ? actin : lanth).push(e);
      continue;
    }
    main.push({ ...e, col: e.group, row: e.period, fBlock: false });
  }
  lanth.sort((a, b) => a.z - b.z);
  actin.sort((a, b) => a.z - b.z);
  const fBlock: Cell[] = [];
  lanth.forEach((e, i) => fBlock.push({ ...e, col: F_BLOCK_START_COL + i, row: 1, fBlock: true }));
  actin.forEach((e, i) => fBlock.push({ ...e, col: F_BLOCK_START_COL + i, row: 2, fBlock: true }));
  return { main, fBlock };
}

// Heat scalar in 0..1 for a cell along the trend's direction. We combine the
// horizontal position (group, 1..18) and vertical position (period, 1..7) using
// the trend's sign so the gradient flows toward where the property is greatest.
function heatValue(period: number, group: number, def: TrendDef): number {
  // Normalize each axis to 0..1 at its "increasing" end.
  const across = (group - 1) / 17; // 0 at group 1, 1 at group 18
  const down = (period - 1) / 6; // 0 at period 1, 1 at period 7
  const a = def.acrossSign === 1 ? across : 1 - across;
  const d = def.downSign === 1 ? down : 1 - down;
  // Average the two axes → a diagonal gradient (the textbook corner-to-corner look).
  return (a + d) / 2;
}

// Two-stop heat ramp from a cool quiet tone to the saturated accent. Uses theme
// tokens via color-mix so it tracks whatever accent the active theme defines.
function heatColor(t: number): string {
  const pct = Math.round(Math.max(0, Math.min(1, t)) * 100);
  return `color-mix(in srgb, var(--ws-accent) ${pct}%, var(--ws-surface-2))`;
}

export default function PeriodicTrends({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);
  const { main, fBlock } = useMemo(buildCells, []);

  const initialKey: PeriodicTrendKey =
    (config.trend && TREND_BY_KEY[config.trend] && config.trend) || 'atomic_radius';
  const [trendKey, setTrendKey] = useState<PeriodicTrendKey>(initialKey);
  const def = TREND_BY_KEY[trendKey] ?? TRENDS[0];

  // reveal 0..1 fades the heat + arrows in (self-demo / cue.progress).
  const [reveal, setReveal] = useState(reduced || hasCue ? 1 : 0);
  const [showTry, setShowTry] = useState(false);
  const [interacted, setInteracted] = useState(false);

  // Self-demo on mount: sweep the heat + arrows in, then offer "Now you try".
  useEffect(() => {
    if (reduced || hasCue) {
      setReveal(1);
      return;
    }
    setReveal(0);
    const start = performance.now();
    const DURATION = 1100;
    let raf = 0;
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / DURATION);
      setReveal(p);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setShowTry(true);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // Arm once on mount; reduced/hasCue read at mount time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cue: highlight a trend name / step index switches the trend; progress drives
  // the reveal so the gradient tracks the narration.
  const cueTokens = useMemo(() => cueHighlights(cue), [cue]);
  const lastCueKey = useRef<string>('');
  useEffect(() => {
    if (!cue) return;
    // Switch trend via highlight token or step index.
    const byToken = resolveTrendToken(cueTokens);
    let nextKey: PeriodicTrendKey | null = byToken;
    if (!nextKey && typeof cue.step === 'number' && TRENDS[cue.step]) {
      nextKey = TRENDS[Math.max(0, Math.min(TRENDS.length - 1, Math.round(cue.step)))].key;
    }
    if (nextKey && nextKey !== trendKey) setTrendKey(nextKey);

    // Progress fades the heat + arrows in WHILE PLAYING; at rest (paused) show the
    // trend fully so the slide isn't a bare grid with no overlay.
    const sig = `${cueTokens.join(',')}|${cue.step ?? ''}|${cue.progress ?? ''}`;
    if (sig !== lastCueKey.current) {
      lastCueKey.current = sig;
      setShowTry(false);
    }
    setReveal(cue.play === true && typeof cue.progress === 'number' ? Math.max(0, Math.min(1, cue.progress)) : 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cue, cueTokens]);

  const markInteracted = (next: PeriodicTrendKey) => {
    setShowTry(false);
    setReveal(1);
    setTrendKey(next);
    if (!interacted) {
      setInteracted(true);
      onComplete?.({ type: 'periodic_trends', trend: next });
    }
  };

  // Geometry for the directional-arrow overlay (drawn in the same coordinate
  // space as the CSS grid via an absolutely-positioned SVG).
  const arrowOpacity = reduced ? 1 : reveal;

  return (
    <VizCard
      eyebrow="Periodic table · Trends"
      title={config.title ?? 'Periodic trends'}
      subtitle="Heat + arrows show where the property increases"
      reducedMotion={reduced}
      hint="Pick a trend — the gradient and arrows point toward where it is greatest"
    >
      <div className="relative flex w-full flex-col gap-3">
        <NowYouTry show={showTry && !reduced && !hasCue} />

        {/* Trend selector */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-[10px] font-medium uppercase tracking-wide text-ws-ink-3">
            Trend
          </span>
          {TRENDS.map((t) => {
            const active = t.key === trendKey;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => markInteracted(t.key)}
                aria-pressed={active}
                className={`rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors ${VIZ_FOCUS} ${
                  active
                    ? 'bg-ws-accent text-ws-on-accent'
                    : 'bg-ws-surface-2 text-ws-ink-2 hover:bg-ws-surface'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Table + arrow overlay */}
        <div className="overflow-x-auto pb-1">
          <div className="relative" style={{ minWidth: '34rem' }}>
            <PeriodicGrid
              main={main}
              fBlock={fBlock}
              def={def}
              reveal={reveal}
              reduced={reduced}
            />
            <ArrowOverlay def={def} opacity={arrowOpacity} reduced={reduced} />
          </div>
        </div>

        {/* Rule caption + heat scale */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-semibold text-ws-ink">
            {def.label}{' '}
            <span className="font-normal text-ws-accent">{def.rule.toLowerCase()}</span>
          </p>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-ws-ink-3">low</span>
            <div
              className="h-2.5 w-28 rounded-full"
              style={{
                background: `linear-gradient(to right, ${heatColor(0)}, ${heatColor(0.5)}, ${heatColor(1)})`,
              }}
            />
            <span className="text-[10px] text-ws-ink-3">high</span>
          </div>
        </div>

        <p className="text-[11px] leading-snug text-ws-ink-2">{def.blurb}</p>
      </div>
    </VizCard>
  );
}

// ── The grid of element cells (main 18×7 block + f-block strip) ───────────────
function PeriodicGrid({
  main,
  fBlock,
  def,
  reveal,
  reduced,
}: {
  main: Cell[];
  fBlock: Cell[];
  def: TrendDef;
  reveal: number;
  reduced: boolean;
}) {
  const transition = reduced ? undefined : 'background-color 360ms ease-out, opacity 360ms ease-out';

  const renderCell = (e: Cell) => {
    const t = heatValue(e.period, e.group ?? F_BLOCK_START_COL, def);
    // The reveal sweeps cells in along the gradient: cells nearer the "high"
    // corner light up slightly later, giving a directional wipe.
    const cellReveal = Math.max(0, Math.min(1, (reveal - t * 0.5) / 0.5));
    const lit = reduced ? 1 : cellReveal;
    const bg = heatColor(t * (reduced ? 1 : lit));
    return (
      <div
        key={e.z}
        title={`${e.name} (${e.symbol}) · period ${e.period}, group ${e.group ?? '—'}`}
        style={{
          gridColumn: e.col,
          gridRow: e.row,
          backgroundColor: bg,
          opacity: 0.4 + 0.6 * lit,
          transition,
        }}
        className="flex aspect-square min-w-0 select-none flex-col items-center justify-center rounded-[3px] px-0.5 leading-none"
      >
        <span className="text-[6px] font-medium" style={{ color: DARK_INK_SOFT }}>
          {e.z}
        </span>
        <span className="text-[10px] font-bold" style={{ color: DARK_INK }}>
          {e.symbol}
        </span>
      </div>
    );
  };

  return (
    <>
      <div
        className="grid gap-[2px]"
        style={{
          gridTemplateColumns: 'repeat(18, minmax(1.5rem, 1fr))',
          gridTemplateRows: 'repeat(7, auto)',
        }}
      >
        {main.map(renderCell)}
      </div>
      {/* f-block strip, indented to sit under group 3 */}
      <div
        className="mt-[2px] grid gap-[2px]"
        style={{
          gridTemplateColumns: 'repeat(18, minmax(1.5rem, 1fr))',
          gridTemplateRows: 'repeat(2, auto)',
        }}
      >
        {fBlock.map(renderCell)}
      </div>
    </>
  );
}

// ── Big directional arrows overlaid on the table ──────────────────────────────
// "across" arrow runs along the top edge; "down" arrow runs along the left edge.
// Each points toward the INCREASING direction for the active trend.
function ArrowOverlay({
  def,
  opacity,
  reduced,
}: {
  def: TrendDef;
  opacity: number;
  reduced: boolean;
}) {
  // Use a 0..100 viewBox so the SVG scales with the grid above it.
  const stroke = 'var(--ws-accent)';
  const transition = reduced ? undefined : 'opacity 300ms ease-out';

  // Horizontal arrow: along the top. Points right when across increases L→R.
  const acrossRight = def.acrossSign === 1;
  // Vertical arrow: along the left. Points down when down-sign increases T→B.
  const downDown = def.downSign === 1;

  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      className="pointer-events-none absolute inset-0 h-full w-full"
      style={{ opacity, transition }}
      aria-hidden="true"
    >
      <defs>
        <marker
          id="pt-arrow-h"
          markerWidth="6"
          markerHeight="6"
          refX="3"
          refY="3"
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <path d="M0,0 L6,3 L0,6 Z" fill={stroke} />
        </marker>
        <marker
          id="pt-arrow-v"
          markerWidth="6"
          markerHeight="6"
          refX="3"
          refY="3"
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <path d="M0,0 L6,3 L0,6 Z" fill={stroke} />
        </marker>
      </defs>
      {/* Across-period arrow (top edge) */}
      <line
        x1={acrossRight ? 6 : 94}
        y1={4}
        x2={acrossRight ? 94 : 6}
        y2={4}
        stroke={stroke}
        strokeWidth={1.4}
        strokeLinecap="round"
        markerEnd="url(#pt-arrow-h)"
        vectorEffect="non-scaling-stroke"
      />
      {/* Down-group arrow (left edge) */}
      <line
        x1={4}
        y1={downDown ? 8 : 92}
        x2={4}
        y2={downDown ? 92 : 8}
        stroke={stroke}
        strokeWidth={1.4}
        strokeLinecap="round"
        markerEnd="url(#pt-arrow-v)"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
